/**
 * @graph-stack web-design-system
 * @graph-domain UX
 * @graph-business ユーザーアバターコンポーネント。画像URL表示、エラー時にイニシャルフォールバック、5段階サイズ対応
 * @graph-connects none
 */
import { forwardRef, useState, type ImgHTMLAttributes } from 'react';
import { cn } from '../../../lib/cn';

export interface AvatarProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'size'> {
  /** 画像URL */
  src?: string;
  /** 代替テキスト */
  alt?: string;
  /** フォールバック時の表示名（イニシャル表示用） */
  name?: string;
  /** アバターのサイズ */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

/** @graph-connects none */
const sizeStyles = {
  xs: 'w-6 h-6 text-xs',
  sm: 'w-8 h-8 text-sm',
  md: 'w-10 h-10 text-base',
  lg: 'w-12 h-12 text-lg',
  xl: 'w-16 h-16 text-xl',
};

/** @graph-connects none */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** @graph-connects none */
export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(
  ({ src, alt, name, size = 'md', className, ...props }, ref) => {
    const [hasError, setHasError] = useState(false);
    const showImage = src && !hasError;
    const initials = name ? getInitials(name) : '?';

    return (
      <span
        ref={ref}
        className={cn(
          'relative inline-flex items-center justify-center rounded-full overflow-hidden bg-[var(--color-accent-muted)] text-[var(--color-accent-primary)] font-medium',
          sizeStyles[size],
          className,
        )}
      >
        {showImage ? (
          <img
            src={src}
            alt={alt ?? name ?? 'Avatar'}
            className="w-full h-full object-cover"
            onError={() => {
              setHasError(true);
            }}
            {...props}
          />
        ) : (
          <span aria-label={alt ?? name ?? 'Unknown user'}>{initials}</span>
        )}
      </span>
    );
  },
);

Avatar.displayName = 'Avatar';
